import React, { useMemo, useState, useRef, createContext, useContext, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Avatar from '@albertsons/uds/molecule/Avatar';
import MainComponent from '@albertsons/uds/layout/Main';
import { authProvider } from '../utils/authProvider';
import { Outlet, useNavigate, useLocation } from 'react-router';
import {
    Home as DashboardIcon,
    UserPlus as SelfServiceIcon,
    FileInput as FileInputIcon,
    Settings as SettingsIcon,
    Database as DataLoadIcon,
    LogOut as LogOutIcon,
    Box as IEHubIcon
} from 'lucide-react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ErrorModal from '../common/ErrorModal';
import CommandPalette from '../CommandPalette';
import routeImage from '../../images/route.png';
import getServiceGroups from '../services/layoutServices/getServiceGroups';
import { useUserPermissions } from '../context/UserPermissionsContext';
import { savePreferredDC, normalizeCapabilityKey } from '../services/settingsServices/userRolesService';
import { DC_CHANGE_EVENT, broadcastDCChange } from '../utils/dcEvents';


// Toast Context
interface ToastContextType {
    showSuccess: (msg?: string) => void;
    showError: (msg?: string, fullErrors?: string[]) => void;
    showWarning: (msg?: string) => void;
    clearToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a Layout component');
    }
    return context;
};

const Layout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = authProvider();
    const { name, userId: authUserId } = auth;
    const userName = name ? name.split(' (')[0] : '';

    const { userPermissions, isActive } = useUserPermissions();

    // Toast message state and handlers
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
    const [toastOpen, setToastOpen] = useState(false);
    const [fullErrorList, setFullErrorList] = useState<string[]>([]);
    const [showViewAllErrors, setShowViewAllErrors] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [toastId, setToastId] = useState(0);
    const [ignoreNextClick, setIgnoreNextClick] = useState(false);
    const [isPaletteOpen, setIsPaletteOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
    const avatarRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [popoverPos, setPopoverPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });

    const showSuccess = (msg: string = 'Operation completed successfully!') => {
        setToastMessage(msg);
        setToastSeverity('success');
        setToastOpen(true);
        setShowViewAllErrors(false);
        setFullErrorList([]);
        setIgnoreNextClick(true);
        setTimeout(() => setIgnoreNextClick(false), 100);
    };

    const showWarning = (msg: string = 'Warning: Please check your input.') => {
        setToastMessage(msg);
        setToastSeverity('warning');
        setToastOpen(true);
        setShowViewAllErrors(false);
        setFullErrorList([]);
        setIgnoreNextClick(true);
        setTimeout(() => setIgnoreNextClick(false), 100);
    };

    const showError = (msg: string = 'An error occurred during the operation.', fullErrors?: string[]) => {
        if (fullErrors && fullErrors.length > 1) {
            // Extract prefix from the main message by finding the first individual error
            let prefix = msg;
            if (fullErrors[0] && msg.includes(fullErrors[0])) {
                prefix = msg.substring(0, msg.indexOf(fullErrors[0]));
            }
           // const errorCount = fullErrors.filter(e => /^Error.*line /i.test(e) || /^Error/i.test(e)).length;
            const errorCount = fullErrors.filter(e => /^Error.*line /i.test(e) || /^Error in Shipment ID.*/i.test(e)).length;
            const moreCount = errorCount - 1;
            // Show truncated message with prefix preserved and "View All Errors" button
            const truncatedMessage = `${prefix}${fullErrors[0]} ... and ${moreCount} more error${moreCount !== 1 ? 's' : ''}`;
            setToastMessage(truncatedMessage);
            setFullErrorList(fullErrors);
            setShowViewAllErrors(true);
        } else {
            setToastMessage(msg);
            setShowViewAllErrors(false);
            setFullErrorList(fullErrors || []);
        }
        setToastSeverity('error');
        setToastOpen(true);
        setToastId(prev => prev + 1);
        setIgnoreNextClick(true);
        setTimeout(() => setIgnoreNextClick(false), 100);
    };

    const clearToast = () => {
        setToastOpen(false);
        setShowViewAllErrors(false);
        setToastMessage(null);
        setFullErrorList([]);
    };

    // Close toast on button or menu clicks
    React.useEffect(() => {
        const handleClick = (e: Event) => {
            const target = e.target as Element;
            if (toastOpen && !ignoreNextClick && (target.tagName === 'BUTTON' || target.closest('[role="menuitem"]') || target.closest('.MuiMenuItem-root'))) {
                clearToast();
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [toastOpen, ignoreNextClick]);

    // Close user menu when clicking outside (portal-aware: check both avatar and popover refs)
    React.useEffect(() => {
        if (!userMenuOpen) return;
        const handleOutsideClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (!avatarRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [userMenuOpen]);

    // Clear toast on navigation
    React.useEffect(() => {
        clearToast();
    }, [location]);

    const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setToastOpen(false);
        setShowViewAllErrors(false);
    };

    const handleViewAllErrors = () => {
        setErrorModalOpen(true);
        // Close the toast when modal opens for better UX
        setToastOpen(false);
        setShowViewAllErrors(false);
    };

    const handleCloseErrorModal = () => {
        setErrorModalOpen(false);
    };

    // Get initial DC - empty string, will be set by API's preferredDc or availableDCs
    const getInitialDC = () => {
        return '';
    };

    const [selectedDC, setSelectedDC] = React.useState(getInitialDC());
    const [selfServiceGroups, setSelfServiceGroups] = useState<Record<string, string[]>>({});

    // All DCs available to this user (for the preferred DC picker)
    const availableDCs = useMemo(() => {
        if (!userPermissions) return [];
        const dcSet = new Set<string>();
        (userPermissions.roles || []).forEach(role => { if (role.dcCode) dcSet.add(role.dcCode); });
        return Array.from(dcSet).sort();
    }, [userPermissions]);

    const [preferredDCSelection, setPreferredDCSelection] = useState<string>('');
    const [dcSaved, setDcSaved] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);

    // Initialize DC from API's preferredDc or default to first available DC (only on first load)
    useEffect(() => {
        if (hasInitialized || !userPermissions || availableDCs.length === 0) return;

        let dcToSet = '';
        // The Preferred DC shown in the avatar menu defaults to what was last saved
        // successfully from this browser, so it reflects the save immediately even if
        // the backend hasn't caught up yet on a subsequent fresh load.
        let preferredDcToShow = '';
        const lastSaved = localStorage.getItem('lastSavedPreferredDC') || '';
        const sessionDC = sessionStorage.getItem('selectedDC');
        if (sessionDC && availableDCs.includes(sessionDC)) {
            dcToSet = sessionDC;
        } else if (userPermissions.preferredDc && availableDCs.includes(userPermissions.preferredDc)) {
            dcToSet = userPermissions.preferredDc;
        } else {
            // Default to first available DC
            dcToSet = availableDCs[0];
        }

        // The avatar's "Preferred DC" picker shows the last value the user saved from this
        // browser, if any and still valid; otherwise falls back to whatever we resolved above.
        preferredDcToShow = lastSaved && availableDCs.includes(lastSaved) ? lastSaved : dcToSet;

        setSelectedDC(dcToSet);
        setPreferredDCSelection(preferredDcToShow);
        sessionStorage.setItem('selectedDC', dcToSet);
        setHasInitialized(true);
    }, [userPermissions, availableDCs, hasInitialized]);

    const handleSavePreferredDC = async () => {
        if (!preferredDCSelection || !authUserId) return;
        try {
            await savePreferredDC(authUserId, preferredDCSelection);
            // Remember what was saved so the avatar menu shows it on next fresh load,
            // even if the backend hasn't propagated the change yet.
            localStorage.setItem('lastSavedPreferredDC', preferredDCSelection);
            // Make the newly preferred DC the active one everywhere immediately.
            setSelectedDC(preferredDCSelection);
            sessionStorage.setItem('selectedDC', preferredDCSelection);
            broadcastDCChange(preferredDCSelection);
        } catch (err) {
            console.error('Failed to save preferred DC to backend:', err);
            showError('Failed to save preferred DC. Please try again.');
            return;
        }
        setDcSaved(true);
        setTimeout(() => {
            setDcSaved(false);
            setUserMenuOpen(false);
            // Reload so every page's own DC dropdown re-initializes from the new selectedDC
            window.location.reload();
        }, 1500);
    };

    // Persist selected DC to session storage whenever it changes (retained till refresh)
    useEffect(() => {
        if (selectedDC) {
            sessionStorage.setItem('selectedDC', selectedDC);
        }
    }, [selectedDC]);

    // Fetch self-service groups from API
    useEffect(() => {
        if (selectedDC) {
            getServiceGroups(selectedDC, () => {}, setSelfServiceGroups);
        } else {
            console.log('Layout: No DC selected, skipping API call');
        }
    }, [selectedDC]);   
    
    // Listen for DC changes made by any page's own DC dropdown (only after API initialization)
    React.useEffect(() => {
        if (!hasInitialized) return;

        const applyDC = (newDC: string) => {
            if (!newDC) return;
            // Apply WWEL migration on changes too
            if (newDC === 'WEL') {
                sessionStorage.setItem('selectedDC', 'WWEL');
                setSelectedDC('WWEL');
            } else {
                setSelectedDC(newDC);
            }
        };

        // Cross-tab: native 'storage' event (does not fire in the tab that made the change)
        const handleStorageChange = () => applyDC(sessionStorage.getItem('selectedDC') || '');

        // Same-tab: pages with their own DC dropdown broadcast this after updating sessionStorage
        const handleDCBroadcast = (e: Event) => applyDC((e as CustomEvent<string>).detail);

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener(DC_CHANGE_EVENT, handleDCBroadcast);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener(DC_CHANGE_EVENT, handleDCBroadcast);
        };
    }, [hasInitialized]);

    // Check if user has capability for the selected DC (must have selectedDC set).
    // Capability keys are normalized (spaces/underscores/hyphens stripped) so keys like
    // 'camsrouting' still match API values like 'CAMS Routing'.
    const hasCapability = useCallback((capability: string): boolean => {
        if (!userPermissions || !selectedDC) return false;
        const target = normalizeCapabilityKey(capability);
        const rolesToCheck = userPermissions.roles.filter((role: { dcCode: string }) => role.dcCode === selectedDC);
        return rolesToCheck.some((role: { capabilities?: string[] }) =>
            (role.capabilities || []).some((cap: string) => normalizeCapabilityKey(cap).includes(target))
        );
    }, [userPermissions, selectedDC]);

    // Check if user has access to specific operations for the currently selected DC
    const hasXDockAccess = useMemo(() => {
        return hasCapability('xdock');
    }, [hasCapability]);

    const hasIN02Access = useMemo(() => {
        return hasCapability('in02');
    }, [hasCapability]);

    const hasCamsRoutingAccess = useMemo(() => {
        return hasCapability('camsrouting');
    }, [hasCapability]);

    const hasAnyDCFileUploadAccess = useMemo(() => {
        return hasXDockAccess || hasIN02Access;
    }, [hasXDockAccess, hasIN02Access]);

    const hasCamsRtngAccess = useMemo(() => {
        return hasCamsRoutingAccess;
    }, [hasCamsRoutingAccess]);

    // Check if user has Self Service access
    const hasSelfServiceAccess = useMemo(() => {
        return hasCapability('self_service');
    }, [hasCapability]);

    // Matched on just 'banana' (not the full 'banana_sequencing' phrase) since the
    // capability name from the API has been seen with typos/variants (e.g. "BANANA SEQENCING").
    const hasBananaSequencingAccess = useMemo(() => {
        return hasCapability('banana');
    }, [hasCapability]);

    const hasIeHubAccess = useMemo(() => {
        return hasCapability('iehub');
    }, [hasCapability]);

    const menu = [
        {
            id: 'dashboard',
            icon: <DashboardIcon />,
            label: 'Dashboard',
            favorite: true,
            onClick: () => navigate('/wms-upload-ui/dashboard'),
        },
        // Only show Routing if user has access
        ...(hasCamsRtngAccess ? [{
            id: 'routing',
            icon: <img src={routeImage} alt="Route" style={{ width: '22px', height: '22px', filter: 'brightness(0) invert(1)' }} />,
            label: 'Routing',
            onClick: () => navigate('/wms-upload-ui/routing'),
        }] : []),
        // Only show DC File Upload if user has access to any DC operations
        ...(hasAnyDCFileUploadAccess ? [{
            id: 'dc_file_upload',
            icon: <FileInputIcon />,
            label: 'DC File Upload',
            menu: [
                // Only show XDOCK Upload if user has access
                ...(hasXDockAccess ? [{
                    id: 'xdock_upload',
                    label: 'XDOCK Upload',
                    onClick: () => navigate('/wms-upload-ui/dc-file-upload/xdock-upload'),
                }] : []),
                // Only show IN02 Upload if user has access
                ...(hasIN02Access ? [{
                    id: 'in02_upload',
                    label: 'Shipment Planning Upload (IN02)',
                    onClick: () => navigate('/wms-upload-ui/dc-file-upload/in02-upload'),
                }] : []),
            ].filter(item => item) // Remove any null/undefined items
        }] : []),
        // Only show Self Service if user has access
        ...((hasSelfServiceAccess || hasBananaSequencingAccess) ? [{
            id: 'self_service',
            icon: <SelfServiceIcon />,
            label: 'Self Service',
            // Build a flat list: [Group header, Sub1, Sub2, Group header, Sub1, ...]
            menu: [
    {
        id: 'self_service_api_runner',
        label: 'API Runner',
        onClick: () => navigate('/wms-upload-ui/self-service/api-runner'),
    },
    ...Object.entries(selfServiceGroups).flatMap(([groupName, subGroups]) => {

        // Apply access control from bananaSeq branch
        if (groupName.toLowerCase() === 'shipping' && !hasSelfServiceAccess) {
            return [];
        }

        if (groupName.toLowerCase() === 'location' && !hasBananaSequencingAccess) {
            return [];
        }

        const groupHeader = {
            id: `self_service_${groupName.replace(/\s+/g, '_')}`,
            label: groupName,
            disabled: true,
            className: 'uds-menu-group',
        };

        const subListItems = subGroups.map((subGroup) => ({
            id: `self_service_${groupName.toLowerCase().replace(/\s+/g, '_')}_${subGroup.toLowerCase().replace(/\s+/g, '_')}`,
            label: `\u00A0\u00A0\u00A0\u00A0\u00A0${subGroup}`,
            onClick: () =>
                navigate(
                    `/wms-upload-ui/self-service/self-service-context/${groupName
                        .toLowerCase()
                        .replace(/\s+/g, '-')}/${subGroup
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`,
                    {
                        state: { groupName, subGroup },
                    }
                ),
        }));

        return [groupHeader, ...subListItems];
    })
] 
        }] : []),
        ...(selectedDC ? [{
            id: 'data_load',
            icon: <DataLoadIcon />,
            label: 'Data Load',
      menu: [
        {
          id: "l1interfaces",
          label: "L1 Interfaces",
          menu: [
            {
              id: "item",
              label: "Item",
              onClick: () => navigate("/wms-upload-ui/data-load/item"),
            },
            {
              id: "retail_store",
              label: "Retail Store",
              onClick: () => navigate("/wms-upload-ui/data-load/retail-store"),
            },
            {
              id: "wholesale_store",
              label: "Wholesale Store",
              onClick: () =>
                navigate("/wms-upload-ui/data-load/wholesale-store"),
            },
            {
              id: "transfer_customer",
              label: "Transfer Customer",
              onClick: () =>
                navigate("/wms-upload-ui/data-load/transfer-customer"),
            },
            {
              id: "vendor_facility",
              label: "Vendor Facility",
              onClick: () =>
                navigate("/wms-upload-ui/data-load/vendor-facility"),
            },
            {
              id: "vendor",
              label: "Vendor",
              onClick: () => navigate("/wms-upload-ui/data-load/vendor"),
            },
          ],
        },
        {
          id: "l2interfaces",
          label: "L2 Interfaces",
          menu: [
            {
              id: "master_data_interfaces",
              label: "Master Data Interfaces",
              // onClick: () => navigate('/wms-upload-ui/data-load/store-order'),
              menu: [
                {
                  id: "item_facility",
                  label: "Item Facility",
                  onClick: () =>
                    navigate("/wms-upload-ui/data-load/item-facility"),
                },
                {
                  id: "item_substitution",
                  label: "Item Substitution",
                  onClick: () => navigate("/wms-upload-ui/data-load/item-sub"),
                },
                {
                  id: "item_location_assignment",
                  label: "Item Location Assignment",
                  onClick: () =>
                    navigate("/wms-upload-ui/data-load/location-item-asmt"),
                },
                {
                  id: "active_inventory",
                  label: "Active Inventory",
                  onClick: () =>
                    navigate("/wms-upload-ui/data-load/active-inventory"),
                },
                {
                  id: "lpn_inventory",
                  label: "LPN Inventory",
                  onClick: () => navigate("/wms-upload-ui/data-load/lpn-sat"),
                },
              ],
            },
            {
              id: "transactional_interfaces",
              label: "Transactional Interfaces",
              menu: [
                {
                  id: "store_order",
                  label: "Store Order",
                  onClick: () =>
                    navigate("/wms-upload-ui/data-load/store-order"),
                },
                {
                  id: "appointment",
                  label: "Appointment",
                  onClick: () =>
                    navigate("/wms-upload-ui/data-load/appointment"),
                },
                {
                  id: "purchase_order",
                  label: "Purchase Order",
                  onClick: () =>
                    navigate("/wms-upload-ui/data-load/purchase-order"),
                },
              ],
        },
          ],
        },
      ],
    }] : []),
    // Only show IE Hub if user has access
    ...(hasIeHubAccess ? [{
        id: 'ie_hub',
        icon: <IEHubIcon />,
        label: 'IE Hub',
        menu: [
            {
                id: 'a3_builder',
                label: 'A3 Builder',
                onClick: () => navigate('/wms-upload-ui/ie-hub/a3-builder'),
            },
            {
                id: 'a3_repository',
                label: 'A3 Repository',
                onClick: () => navigate('/wms-upload-ui/ie-hub/a3-repository'),
            },
        ],
    }] : []),
    // Settings menu is visible to all users; Users/Groups/Permission pages
    // each gate admin-only actions (add/edit/delete) internally via isAdmin.
    {
        id: 'settings',
        icon: <SettingsIcon />,
        label: 'Settings',
        menu: [
            {
                id: 'users',
                label: 'Users',
                onClick: () => navigate('/wms-upload-ui/settings/users'),
            },
            {
                id: 'groups',
                label: 'Groups',
                onClick: () => navigate('/wms-upload-ui/settings/groups'),
            },
            {
                id: 'permission',
                label: 'Permission',
                onClick: () => navigate('/wms-upload-ui/settings/permission'),
            },
        ]
    },
    ];

    return (
        <>
            {/* Global Toast Messages */}
            <Snackbar 
                key={toastId}
                open={toastOpen} 
                autoHideDuration={null} 
                onClose={handleToastClose} 
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleToastClose} 
                    severity={toastSeverity} 
                    sx={{ 
                        width: '100%',
                        '& .MuiAlert-message': {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 1
                        },
                        '& .MuiAlert-action': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }
                    }}
                    action={showViewAllErrors ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                                onClick={handleViewAllErrors}
                                style={{
                                    background: 'none',
                                    border: '1px solid #dc2626',
                                    color: '#dc2626',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                            >
                                View All Errors
                            </button>
                            <button
                                onClick={clearToast}
                                style={{
                                    background: 'none',
                                    border: '1px solid #666',
                                    color: '#666',
                                    padding: '4px 6px',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '24px',
                                    height: '24px'
                                }}
                                title="Close"
                            >
                                ×
                            </button>
                        </div>
                    ) : null}
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
            
            {/* Error Modal */}
            <ErrorModal
                open={errorModalOpen}
                onClose={handleCloseErrorModal}
                errors={fullErrorList}
                title="All Validation Errors"
            />
            
            <MainComponent
                title='Warehouse Management System'
                heading='WMS Operations Hub'
                menu={menu}
            >
            <MainComponent.Header>
                {
                    ({ toggle }) => (
                        <>
                            {/* User menu — avatar trigger + portal popover */}
                            <div ref={avatarRef}>
                                <div
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        if (!userMenuOpen && avatarRef.current) {
                                            const rect = avatarRef.current.getBoundingClientRect();
                                            setPopoverPos({
                                                top: rect.bottom + 8,
                                                right: window.innerWidth - rect.right,
                                            });
                                        }
                                        setUserMenuOpen(prev => !prev);
                                    }}
                                >
                                    <Avatar
                                        full
                                        className='ml-3 max-ss:hidden'
                                        src=''
                                        name={userName}
                                        status='online'
                                    />
                                </div>

                                {userMenuOpen && ReactDOM.createPortal(
                                    <div ref={popoverRef} style={{
                                        position: 'fixed',
                                        top: popoverPos.top,
                                        right: popoverPos.right,
                                        background: 'white',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                        padding: '16px',
                                        minWidth: '240px',
                                        zIndex: 9999
                                    }}>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                                            {userName}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '14px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                            {auth.mail}
                                        </div>

                                        <div style={{ fontSize: '12px', color: '#475569', marginBottom: '6px', fontWeight: 500 }}>
                                            Preferred DC
                                        </div>
                                        <select
                                            value={preferredDCSelection}
                                            onChange={e => setPreferredDCSelection(e.target.value)}
                                            style={{
                                                width: '100%',
                                                fontSize: '13px',
                                                padding: '6px 8px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                background: 'white',
                                                cursor: 'pointer',
                                                marginBottom: '10px',
                                                outline: 'none'
                                            }}
                                        >
                                            <option value="">Select DC</option>
                                            {availableDCs.map(dc => (
                                                <option key={dc} value={dc}>{dc}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => { handleSavePreferredDC(); }}
                                            disabled={!preferredDCSelection}
                                            style={{
                                                width: '100%',
                                                fontSize: '13px',
                                                padding: '7px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                background: dcSaved ? '#16a34a' : '#0051a1',
                                                color: 'white',
                                                cursor: preferredDCSelection ? 'pointer' : 'not-allowed',
                                                opacity: preferredDCSelection ? 1 : 0.5,
                                                transition: 'background 0.3s',
                                                fontWeight: 500
                                            }}
                                        >
                                            {dcSaved ? '✓ Saved' : 'Save'}
                                        </button>

                                        <div style={{ borderTop: '1px solid #f1f5f9', marginTop: '14px', paddingTop: '10px' }}>
                                            <button
                                                onClick={() => auth.logout()}
                                                style={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    fontSize: '13px',
                                                    padding: '7px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #dc2626',
                                                    background: 'white',
                                                    color: '#dc2626',
                                                    cursor: 'pointer',
                                                    fontWeight: 500
                                                }}
                                            >
                                                <LogOutIcon size={14} />
                                                Logout
                                            </button>
                                        </div>
                                    </div>,
                                    document.body
                                )}
                            </div>
                        </>
                    )
                }
            </MainComponent.Header>
            <MainComponent.Content>
                <ToastContext.Provider value={{ showSuccess, showError, showWarning, clearToast }}>
                    {!isActive ? (
                        <div style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', height: '60vh', gap: '16px',
                            fontFamily: '"Segoe UI", Arial, sans-serif', color: '#344054',
                            textAlign: 'center', padding: '24px',
                        }}>
                            <div style={{ fontSize: '56px', lineHeight: 1 }}>🚫</div>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1d2939' }}>
                                Account Inactive
                            </h2>
                            <p style={{ margin: 0, color: '#667085', fontSize: '14px', maxWidth: '360px' }}>
                                Your account has been deactivated. Please contact your administrator to regain access.
                            </p>
                        </div>
                    ) : (
                        <Outlet />
                    )}
                </ToastContext.Provider>
            </MainComponent.Content>
        </MainComponent>
        
        {/* Help button - only shows on app pages */}
        <div
          style={{
            position: 'fixed',
            top: '12px',
            right: '342px',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <button
            aria-label="Help"
            style={{
              background: 'linear-gradient(135deg, #00b4d8 60%, #0051a1 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              fontSize: '1.2em',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Help"
            onClick={() => {setIsPaletteOpen(true)}}
            onMouseOver={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.10)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.22)';
            }}
            onMouseOut={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
          >
            <span role="img" aria-label="Help" style={{ fontSize: '1.2em' }}>?</span>
          </button>
        </div>
        
        <CommandPalette isOpen={isPaletteOpen} setIsOpen={setIsPaletteOpen} />
        </>
    );
};

export default Layout;